import React, { useEffect, useCallback, useState } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";

import AutoCompleteSearchBar from "./auto-complete-search-bar";

const mobileSearchBarStyle = css({
    marginInlineStart: "25px",
    marginInlineEnd: "25px",
    width: "100%"
});

const productNavSearchBarStyle = css({
    width: "100%",

    [media.above.sm]: {
        maxWidth: "1152px"
    }
});

const globalNavSearchBarStyle = css({
    marginInlineStart: "25px",
    marginInlineEnd: "25px",
    width: "100%"
});

export type SearchBarProps = {
    readonly isOpenSearch: boolean;
    readonly setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    readonly isMobile?: boolean;
    parentType?: "GlobalNav" | "ProductNav";
    aiEnabled?: boolean;
};

const SearchBar = ({
    isOpenSearch,
    setIsOpenSearch,
    isMobile = false,
    parentType = "GlobalNav"
}: SearchBarProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const wacAISearchSummaryExperiment = useExperiment(
        experiments.wacAISearchSummaryV2
    );
    const [renderSearchButton, setRenderSearchButton] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);

    const handleEscKeyEvent = useCallback((event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsOpenSearch(false);

            createAnalyticsEvent({
                eventName: "searchClose",
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: "searchBarEscapeKey",
                source: "globalNav",
                attributes: {
                    product: "search",
                    pageUrl: window.location.href,
                    pagePath: window.location.pathname
                }
            }).fire();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent]);

    useEffect(() => {
        if (
            wacAISearchSummaryExperiment &&
            wacAISearchSummaryExperiment.type !== "indeterminate"
        ) {
            setRenderSearchButton(true);
        }

        if (
            wacAISearchSummaryExperiment &&
            wacAISearchSummaryExperiment.type === "enrolled" &&
            wacAISearchSummaryExperiment.value
        ) {
            setAiEnabled(true);
        }
    }, [wacAISearchSummaryExperiment]);

    if (!renderSearchButton) {
        return null;
    }

    return (
        <div
            className="search-bar"
            data-testid="search-bar"
            css={[
                css({
                    marginInlineStart: "25px",
                    marginInlineEnd: "25px",
                    width: "100%"
                }),
                isMobile ? mobileSearchBarStyle : "",
                parentType === "ProductNav"
                    ? productNavSearchBarStyle
                    : globalNavSearchBarStyle
            ]}
        >
            <AutoCompleteSearchBar
                isOpenSearch={isOpenSearch}
                setIsOpenSearch={setIsOpenSearch}
                aiEnabled={aiEnabled}
                parentType={parentType}
            />
        </div>
    );
};

export default SearchBar;
